import type { EntityCtx } from '@cli-types/next-adapter/generators.js'
import { toKebabCase } from '@cli-utils/text.js'
import { afterHook } from '@next-adapter/generators/actions/lifecycle-hook-emissions/after-hook.js'
import { beforeHook } from '@next-adapter/generators/actions/lifecycle-hook-emissions/before-hook.js'
import { lifecycleContext } from '@next-adapter/generators/actions/lifecycle-hook-emissions/lifecycle-context.js'
import { lifecycleHookImport } from '@next-adapter/generators/actions/lifecycle-hook-emissions/lifecycle-hook-import.js'
import { renderGenUpdateContentServerActionTemplate } from '@next-adapter/templates/renderers/generators/actions/entity-file-contents/gen-update-content.js'

export function genUpdateContent(ctx: EntityCtx): string {
  const kebabSingular = toKebabCase(ctx.singular)
  const slugSourceValue = ctx.slugSourceField
    ? `updateData.${ctx.slugSourceField} ?? previousRow.${ctx.slugSourceField}`
    : 'undefined'
  const snapshotImport = ctx.hasM2M
    ? `build${ctx.Singular}VersionSnapshotWithRelationships`
    : `build${ctx.Singular}VersionSnapshot`
  const previousSnapshot = ctx.hasM2M
    ? `await build${ctx.Singular}VersionSnapshotWithRelationships(previousRow as unknown as ${ctx.Singular}, tx)`
    : `build${ctx.Singular}VersionSnapshot(previousRow as unknown as ${ctx.Singular})`
  const markdownImport = ctx.hasHtmlOutput
    ? `\nimport { renderMarkdownSync } from './markdown'`
    : ''
  const htmlUpdateBlock = ctx.hasHtmlOutput
    ? `\n` +
      ctx.htmlOutputFields
        .map(
          (f) =>
            `    if (processedData.${f.name} !== undefined) {\n      processedData.${f.name}Html = renderMarkdownSync(String(processedData.${f.name} || ''))\n    }`
        )
        .join('\n') +
      '\n'
    : ''
  const updateContext = lifecycleContext(
    ctx.plural,
    `,\n      entityId: id,\n      input: updateData as unknown as Record<string, unknown>`
  )
  const updateResultContext = lifecycleContext(
    ctx.plural,
    `,\n      entityId: id,\n      input: updateData as unknown as Record<string, unknown>,\n      data: updated${ctx.Singular}Data as unknown as Record<string, unknown>,\n      changedFields`
  )
  const updateBeforePublishBlock = ctx.hasDraft
    ? `\n    const isPublishing = previousRow.published !== true && updateData.published === true\n    const isUnpublishing = previousRow.published === true && updateData.published === false\n    if (isPublishing) {\n      ${beforeHook('beforePublish', 'lifecycleContext')}\n    }\n    if (isUnpublishing) {\n      ${beforeHook('beforeUnpublish', 'lifecycleContext')}\n    }\n`
    : ''
  const updateAfterPublishBlock = ctx.hasDraft
    ? `\n    if (isPublishing) {\n      ${afterHook('afterPublish', `{ ...updated${ctx.Singular}Context, changedFields: ['published'] }`)}\n    }\n    if (isUnpublishing) {\n      ${afterHook('afterUnpublish', `{ ...updated${ctx.Singular}Context, changedFields: ['published'] }`)}\n    }`
    : ''

  return renderGenUpdateContentServerActionTemplate({
    ctxTableVar: ctx.tableVar,
    ctxCacheInvalidationFn: ctx.cacheInvalidationFn,
    ctxCamelPlural: ctx.camelPlural,
    ctxSingular: ctx.Singular,
    kebabSingular: kebabSingular,
    snapshotImport: snapshotImport,
    lifecycleHookImport: lifecycleHookImport(),
    markdownImport: markdownImport,
    updateContext: updateContext,
    beforeHook: beforeHook('beforeUpdate', 'lifecycleContext'),
    updateBeforePublishBlock: updateBeforePublishBlock,
    slugSourceValue: slugSourceValue,
    toKebabCase: toKebabCase(ctx.singular),
    ctxFieldMeta: ctx.fieldMeta,
    htmlUpdateBlock: htmlUpdateBlock,
    previousSnapshot: previousSnapshot,
    ctxPlural: ctx.plural,
    updateResultContext: updateResultContext,
    updateAfterPublishBlock: updateAfterPublishBlock,
    afterHook: afterHook('afterUpdate', `updated${ctx.Singular}Context`),
    ctxCamelSingular: ctx.camelSingular,
    ctxSingularText: ctx.singular
  })
}
